'use client';

import useSWR from 'swr';
import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { apiFetch } from '../lib/api';
import Card from './Card';
import { useTranslation } from '../lib/i18n';
import { useFormatLocale, getFormatLocale } from '../lib/useNationalEnvironment';
import { useToast } from './Toast';

const MarkdownEditor = dynamic(() => import('./MarkdownEditor'), { ssr: false });

type NoteDto = {
  id: number;
  title: string;
  contentMarkdown: string;
  eventType: string | null;
  eventId: number | null;
  tags: string[];
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
};

type Props = {
  eventType: string;
  eventId: number;
  title?: string;
};

const formatDate = (d?: string | null, locale = getFormatLocale()) => d
  ? new Date(d).toLocaleString(locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—';

export default function EventNotesSection({ eventType, eventId, title }: Props) {
  const { t } = useTranslation();
  const fmtLocale = useFormatLocale();
  const toast = useToast();
  const [showForm, setShowForm] = useState(false);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [saving, setSaving] = useState(false);

  // Link existing note search
  const [linkSearch, setLinkSearch] = useState('');
  const [linkQuery, setLinkQuery] = useState('');
  const [linking, setLinking] = useState(false);
  const linkBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = setTimeout(() => setLinkQuery(linkSearch.trim()), 250);
    return () => clearTimeout(handle);
  }, [linkSearch]);

  const listUrl = `/api/user/notes/by-event/${eventType}/${eventId}`;
  const countUrl = `/api/user/notes/by-event/${eventType}/${eventId}/count`;

  const { data: notes, mutate } = useSWR<NoteDto[]>(listUrl);
  const { data: countData } = useSWR<{ count: number }>(countUrl);

  // Search standalone notes (not yet linked) + all notes matching search
  const linkSearchUrl = linkQuery.length > 0
    ? `/api/user/notes?search=${encodeURIComponent(linkQuery)}&size=20`
    : null;
  const { data: searchResults, isLoading: searchLoading } = useSWR<NoteDto[]>(linkSearchUrl);

  // Filter out notes already linked to this event
  const linkedIds = new Set((notes || []).map(n => n.id));
  const availableNotes = (searchResults || []).filter(n => !linkedIds.has(n.id));

  // Click outside to close link dropdown
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (linkBoxRef.current && !linkBoxRef.current.contains(e.target as Node)) {
        setShowLinkForm(false);
      }
    };
    if (showLinkForm) {
      document.addEventListener('mousedown', onClick);
      return () => document.removeEventListener('mousedown', onClick);
    }
  }, [showLinkForm]);

  const handleAdd = async () => {
    if (!newTitle.trim()) {
      toast.showToast(t('noteTitleRequired'), 'error');
      return;
    }
    setSaving(true);
    try {
      await apiFetch('/api/user/notes', {
        method: 'POST',
        body: JSON.stringify({
          title: newTitle.trim(),
          contentMarkdown: newContent,
          eventType,
          eventId,
          tags: [],
        }),
      });
      toast.showToast(t('noteSaved'), 'success');
      setNewTitle('');
      setNewContent('');
      setShowForm(false);
      mutate();
    } catch (e: any) {
      toast.showToast(e?.message || t('error'), 'error');
    }
    setSaving(false);
  };

  const handleLinkNote = async (noteId: number) => {
    setLinking(true);
    try {
      await apiFetch(`/api/user/notes/${noteId}`, {
        method: 'PUT',
        body: JSON.stringify({
          eventType,
          eventId,
        }),
      });
      toast.showToast(t('noteLinkedSuccess'), 'success');
      setLinkSearch('');
      setShowLinkForm(false);
      mutate();
    } catch (e: any) {
      toast.showToast(e?.message || t('error'), 'error');
    }
    setLinking(false);
  };

  const handleUnlinkNote = async (note: NoteDto) => {
    try {
      await apiFetch(`/api/user/notes/${note.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          eventType: '',
          eventId: null,
        }),
      });
      toast.showToast(t('noteUnlinkedSuccess'), 'success');
      mutate();
    } catch (e: any) {
      toast.showToast(e?.message || t('error'), 'error');
    }
  };

  const noteCount = countData?.count || 0;
  const noteList = notes || [];

  return (
    <div className="mt-2">
      <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
        <h2 className="text-sm font-medium text-wood dark:text-wood">
          {title || t('noteEventNotes')}
          {noteCount > 0 && <span className="text-xs text-wood dark:text-wood ml-1">({noteCount})</span>}
        </h2>
        <div className="flex gap-1">
          <button className="text-xs px-2 py-1 border border-wheat rounded text-bark hover:bg-wheat/30 transition" onClick={() => { setShowLinkForm(!showLinkForm); setShowForm(false); }} data-no-loading>
            {t('noteLinkExisting')}
          </button>
          <button className="text-xs px-2 py-1 border border-wheat rounded text-bark hover:bg-wheat/30 transition" onClick={() => { setShowForm(!showForm); setShowLinkForm(false); }} data-no-loading>
            {showForm ? t('cancel') : t('noteAdd')}
          </button>
        </div>
      </div>

      {/* Link existing note */}
      {showLinkForm && (
        <Card className="p-2 mb-2 dark:bg-cream dark:border-wheat" >
          <div ref={linkBoxRef} className="relative">
            <label className="block text-xs text-wood dark:text-wood mb-1">{t('noteLinkSearchLabel')}</label>
            <input
              type="text"
              value={linkSearch}
              onChange={(e) => setLinkSearch(e.target.value)}
              placeholder={t('noteLinkSearchPlaceholder')}
              className="input w-full text-sm"
              autoFocus
              data-no-loading
            />
            {linkQuery.length > 0 && (
              <div className="mt-2 max-h-60 overflow-y-auto border border-wheat dark:border-wheat rounded">
                {searchLoading && <div className="px-3 py-2 text-xs text-wood dark:text-wood">{t('loading')}…</div>}
                {!searchLoading && availableNotes.length === 0 && (
                  <div className="px-3 py-2 text-xs text-wood dark:text-wood">{t('noteEventNoResults')}</div>
                )}
                {availableNotes.map((note) => (
                  <div
                    key={note.id}
                    className="flex items-center justify-between px-3 py-2 hover:bg-wheat dark:hover:bg-wheat transition border-b border-wheat/50 last:border-0"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-bark dark:text-bark truncate">
                        {note.pinned && <span className="text-moss dark:text-moss mr-1">★</span>}
                        {note.title || t('noteUntitled')}
                      </div>
                      <div className="text-xs text-wood dark:text-wood truncate">
                        {note.contentMarkdown.substring(0, 80)}
                      </div>
                    </div>
                    <button
                      onClick={() => handleLinkNote(note.id)}
                      disabled={linking}
                      className="text-xs px-2 py-1 bg-moss text-cream rounded hover:bg-moss-dark transition shrink-0 ml-2 disabled:opacity-50"
                      data-no-loading
                    >
                      {t('noteLinkButton')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* New note form */}
      {showForm && (
        <Card className="p-2 mb-2 dark:bg-cream dark:border-wheat">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder={t('noteTitlePlaceholder')}
            className="input w-full mb-2"
            maxLength={200}
          />
          <MarkdownEditor
            value={newContent}
            onChange={setNewContent}
            placeholder={t('noteContentPlaceholder')}
            minHeight="200px"
            textareaId={`event-note-editor-${eventType}-${eventId}`}
          />
          <div className="flex justify-end mt-2">
            <button className="btn-primary" onClick={handleAdd} disabled={saving} data-no-loading>
              {saving ? '…' : t('noteSave')}
            </button>
          </div>
        </Card>
      )}

      {noteList.length ? (
        <div className="grid gap-1">
          {noteList.map((note) => (
            <Card key={note.id} className="p-2 dark:bg-cream dark:border-wheat">
              <div className="flex items-center gap-2 flex-wrap">
                {note.pinned && <span className="text-moss dark:text-moss text-xs">★</span>}
                <Link href={`/notes/${note.id}`} className="font-medium text-bark dark:text-bark hover:underline text-xs">
                  {note.title || t('noteUntitled')}
                </Link>
                <span className="text-xs text-wood dark:text-wood ml-auto">{formatDate(note.updatedAt, fmtLocale)}</span>
                <button
                  onClick={() => handleUnlinkNote(note)}
                  className="text-xs text-wood dark:text-wood hover:text-rust transition"
                  title={t('noteUnlinkEvent')}
                  data-no-loading
                >
                  {t('noteUnlink')}
                </button>
              </div>
              <p className="text-xs text-wood dark:text-wood mt-1 whitespace-pre-wrap line-clamp-3">
                {note.contentMarkdown.substring(0, 300)}
              </p>
            </Card>
          ))}
        </div>
      ) : (
        !showForm && !showLinkForm && <Card className="px-3 py-2 text-wood dark:text-wood text-xs dark:bg-cream dark:border-wheat">{t('noteNoNotesForEvent')}</Card>
      )}
    </div>
  );
}
